import type { TwoFactorMethod } from '@chukua/common';
import { useState } from 'react';
import { verifyTwoFactorLogin, type CurrentUser } from '../../../shared/features/session/session.api';
import { ApiError } from '../../../shared/shared.api';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { useLoginEmailCode } from './use-login-email-code';

/** A recovery code (`xxxx-xxxx-xxxx-xxxx`) doesn't fit the six digit boxes, so it gets its own text field. */
export type CodeEntry = 'code' | 'recovery';

/**
 * The login's second step: which of the account's methods is showing, and for email, sending the code. Email
 * sends on its own when it is the method showing, whether first or switched to, but only once; after that
 * "Send another code" does it. Only the newest email's code works (the server replaces it on each send).
 */
export function useLoginCodeStep(onSignedIn: (user: CurrentUser) => void) {
  const [methods, setMethods] = useState<TwoFactorMethod[]>([]);
  const [method, setMethod] = useState<TwoFactorMethod>('totp');
  const [entry, setEntry] = useState<CodeEntry>('code');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const emailCode = useLoginEmailCode(setError);

  /** `hasEmailed` is passed by begin, where this login's delivery is fresh and the state not yet reset. */
  function show(next: TwoFactorMethod, hasEmailed = emailCode.delivery !== 'none') {
    setMethod(next);
    setEntry('code');
    setError(null);
    if (next === 'email' && !hasEmailed) void emailCode.send();
  }

  function begin(accountMethods: TwoFactorMethod[]) {
    setMethods(accountMethods);
    emailCode.reset();
    show(accountMethods[0], false);
  }

  const otherMethod = methods.find((m) => m !== method);

  async function submit(code: string) {
    setError(null);
    setSubmitting(true);
    try {
      onSignedIn(await verifyTwoFactorLogin(code.trim(), method));
    } catch (err) {
      // A lockout says so; anything else stays generic, like the password step.
      setError(
        err instanceof ApiError && err.status === 429 ? (apiErrorMessage(err) ?? 'Too many attempts.') : 'Incorrect code. Try again, or use a recovery code.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return {
    method,
    otherMethod,
    entry,
    delivery: emailCode.delivery,
    displayedCode: emailCode.displayedCode,
    error,
    submitting,
    begin,
    submit,
    switchMethod: () => otherMethod && show(otherMethod),
    toggleRecoveryCode: () => {
      setError(null);
      setEntry((current) => (current === 'code' ? 'recovery' : 'code'));
    },
    resendEmailCode: emailCode.send,
  };
}

export type LoginCodeStep = ReturnType<typeof useLoginCodeStep>;
